import type { Translations } from '../types';

export const fr: Translations = {
  common: {
    home: 'Accueil',
    github: 'GitHub',
    copy: 'Copier',
    clear: 'Effacer',
    input: 'Entrée',
    output: 'Sortie',
    encode: 'Encoder',
    decode: 'Décoder',
    generate: 'Générer',
    convert: 'Convertir',
    result: 'Résultat',
    copiedToClipboard: 'Copié dans le presse-papiers !',
    failedToCopy: 'Échec de la copie',
    language: 'Langue',
  },
  seo: {
    siteDescription: 'Collection gratuite d\'outils de développement axés sur la confidentialité. Formateur JSON, encodeur/décodeur Base64, générateur UUID, générateur de hash, encodeur URL, convertisseur de timestamp et convertisseur de couleurs. Tous les outils fonctionnent hors ligne dans votre navigateur.',
    keywords: [
      'outils de développement',
      'formateur JSON',
      'encodeur Base64',
      'générateur UUID',
      'générateur hash',
      'encodeur URL',
      'convertisseur timestamp',
      'convertisseur couleur',
      'outils en ligne',
      'outils gratuits',
      'confidentialité',
      'outils hors ligne',
    ],
    ogTitle: 'GetDev.Tools - Outils de développement gratuits axés sur la confidentialité',
    ogDescription: 'Collection d\'outils de développement gratuits fonctionnant hors ligne dans votre navigateur. Formateur JSON, Base64, UUID, Hash, encodeur URL et plus.',
  },
  home: {
    title: 'Collection d\'outils pour développeurs',
    subtitle: 'Outils gratuits et respectueux de la vie privée pour les développeurs. Tout le traitement se fait dans votre navigateur.',
    whyTitle: 'Pourquoi GetDev.Tools ?',
    privacyTitle: '🔒 Confidentialité d\'abord',
    privacyDesc: 'Tout le traitement se fait dans votre navigateur. Vos données ne quittent jamais votre appareil.',
    fastTitle: '⚡ Ultra rapide',
    fastDesc: 'Pas d\'aller-retour serveur. Tout s\'exécute localement pour des résultats instantanés.',
    freeTitle: '🆓 Gratuit pour toujours',
    freeDesc: 'Open source et gratuit. Aucune inscription ni paiement requis.',
    footer: '© 2025 GetDev.Tools - Outils pour développeurs respectueux de la vie privée',
    footerSubtitle: 'Tout le traitement se fait dans votre navigateur',
  },
  tools: {
    jsonFormatter: {
      name: 'Formateur JSON',
      description: 'Formater, valider et embellir les données JSON',
      format: 'Formater',
      minify: 'Minifier',
      validate: 'Valider',
      error: 'Erreur',
    },
    base64: {
      name: 'Encodeur/Décodeur Base64',
      description: 'Encoder et décoder les chaînes Base64',
      encodeButton: 'Encoder',
      decodeButton: 'Décoder',
    },
    uuid: {
      name: 'Générateur UUID',
      description: 'Générer des identifiants UUID v4 conformes à la RFC 4122',
      numberOfUuids: 'Nombre d\'UUID à générer',
      generatedUuids: 'UUID générés',
      copyAll: 'Tout copier',
      aboutTitle: 'À propos d\'UUID v4',
      aboutDesc: 'Les UUID (Identifiants Uniques Universels) v4 sont des identifiants de 128 bits générés aléatoirement. La probabilité de collision est extrêmement faible, ce qui les rend adaptés aux systèmes distribués sans coordination centrale.',
    },
    hash: {
      name: 'Générateur de hachage',
      description: 'Générer des hachages SHA-1, SHA-256, SHA-512',
      inputText: 'Texte d\'entrée',
      securityNotice: '⚠️ Avis de sécurité',
      securityDesc: 'SHA-1 est obsolète pour des raisons de sécurité. Utilisez SHA-256 ou SHA-512 pour les applications cryptographiques. MD5 n\'est pas disponible dans les navigateurs modernes en raison de vulnérabilités de sécurité.',
    },
    urlEncoder: {
      name: 'Encodeur/Décodeur URL',
      description: 'Encoder et décoder les chaînes URL',
      encodeUrl: 'Encoder URL',
      encodeComponent: 'Encoder composant',
      encoded: 'Encodé',
      decoded: 'Décodé',
      differenceTitle: '💡 Différence',
      encodeUriDesc: 'encodeURI : Encode une URI complète (n\'encode pas : ; , / ? : @ & = + $ #)',
      encodeComponentDesc: 'encodeURIComponent : Encode un composant URI (encode tous les caractères spéciaux)',
      examples: 'Exemples',
    },
    timestamp: {
      name: 'Convertisseur de timestamp',
      description: 'Convertir entre les timestamps Unix et les dates lisibles',
      currentTime: 'Heure actuelle',
      unixMs: 'Timestamp Unix (ms)',
      unixS: 'Timestamp Unix (s)',
      toDate: 'Timestamp → Date',
      toTimestamp: 'Date → Timestamp',
      useNow: 'Utiliser maintenant',
      aboutTitle: '💡 À propos des timestamps Unix',
      aboutDesc1: '• Timestamp Unix = secondes depuis le 1er janvier 1970 00:00:00 UTC (époque)',
      aboutDesc2: '• 10 chiffres = secondes (ex : 1609459200)',
      aboutDesc3: '• 13 chiffres = millisecondes (ex : 1609459200000)',
      aboutDesc4: '• Formats de date pris en charge : ISO 8601, RFC 2822 ou langage naturel',
    },
    color: {
      name: 'Convertisseur de couleur',
      description: 'Convertir entre les formats HEX, RGB et HSL',
      pickColor: 'Choisir une couleur',
      formatGuide: '💡 Guide des formats de couleur',
      hexDesc: 'HEX : Notation hexadécimale (#RRGGBB)',
      rgbDesc: 'RGB : Valeurs rouge, vert, bleu (0-255)',
      hslDesc: 'HSL : Teinte (0-360°), Saturation (0-100%), Luminosité (0-100%)',
      alphaDesc: 'RGBA/HSLA : Identique à RGB/HSL avec canal Alpha (opacité)',
    },
    jwt: { name: 'Décodeur JWT', description: 'Décoder et inspecter les JSON Web Tokens', inputToken: 'Jeton JWT', header: 'En-tête', payload: 'Charge utile', signature: 'Signature (Base64)', claims: 'Revendications standard', invalidFormat: 'Format JWT invalide', invalidToken: 'Jeton JWT invalide', securityNotice: 'Avis de sécurité', securityDesc: 'Cet outil ne décode que les jetons JWT', aboutTitle: 'À propos de JWT', aboutDesc1: 'JWT = JSON Web Token', aboutDesc2: 'Format : en-tête.charge.signature', aboutDesc3: 'Utilisé pour l\'authentification sécurisée' },
    diff: { name: 'Comparaison de texte', description: 'Comparer deux blocs de texte', originalText: 'Texte original', modifiedText: 'Texte modifié', sideBySide: 'Côte à côte', unified: 'Unifié', noDifference: 'Aucune différence' },
    markdown: { name: 'Aperçu Markdown', description: 'Écrire et prévisualiser Markdown en temps réel', editor: 'Éditeur', preview: 'Aperçu', supportedFeatures: 'Pris en charge : Titres, Listes, Liens, Images, Blocs de code, Tables, etc.' },
    regex: { name: 'Testeur Regex', description: 'Tester les expressions régulières', pattern: 'Expression régulière', flags: 'Drapeaux', testString: 'Chaîne de test', matches: 'Correspondances', noMatches: 'Aucune correspondance', invalidRegex: 'Regex invalide', globalFlag: 'Global (g)', caseInsensitive: 'Insensible à la casse (i)', multiline: 'Multiligne (m)', dotAll: 'Point correspond à nouvelle ligne (s)' },
    qrCode: { name: 'Générateur de code QR', description: 'Générer des codes QR pour URL, texte, WiFi, etc.', inputData: 'Données d\'entrée', qrCodeImage: 'Image du code QR', size: 'Taille (pixels)', errorCorrection: 'Correction d\'erreur', errorLow: 'Faible (~7%)', errorMedium: 'Moyen (~15%)', errorQuartile: 'Quartile (~25%)', errorHigh: 'Élevé (~30%)', templates: 'Modèles rapides', templateUrl: 'URL du site', templateEmail: 'Email', templatePhone: 'Numéro de téléphone', templateSms: 'SMS', templateWifi: 'Réseau WiFi', templateVcard: 'Carte de contact (vCard)', privacyNotice: '🔒 Avis de confidentialité', privacyDesc: 'Les codes QR sont générés à l\'aide d\'une API externe (qrserver.com). Vos données seront envoyées à leur serveur.' },
    lorem: { name: 'Générateur Lorem Ipsum', description: 'Générer du texte de remplissage pour designs et maquettes', generateType: 'Type de génération', paragraphs: 'Paragraphes', sentences: 'Phrases', words: 'Mots', count: 'Nombre', startWithLorem: 'Commencer par "Lorem ipsum"', generatedText: 'Texte généré', aboutTitle: '💡 À propos de Lorem Ipsum', aboutDesc: 'Lorem Ipsum est un texte de remplissage couramment utilisé dans le design et l\'édition.' },
    htmlEntity: { name: 'Encodeur d\'entités HTML', description: 'Encoder et décoder les entités HTML', inputText: 'Texte d\'entrée', encodeBasic: 'Encoder (Basique)', encodeAll: 'Tout encoder', decode: 'Décoder', commonEntities: 'Entités HTML courantes', character: 'Caractère', entity: 'Entité', description2: 'Description', useCases: '💡 Cas d\'usage', useCase1: 'Encoder Basique : Encode uniquement les caractères essentiels (<, >, &, ", \')', useCase2: 'Tout encoder : Encode tous les caractères spéciaux pour une sécurité maximale', useCase3: 'Décoder : Convertit les entités HTML en caractères normaux' },
  },
};

